import React from 'react';
import { Plus, X } from 'lucide-react';

interface CountriesFieldProps {
  label: string;
  name: string;
  value: string[];
  onChange: (countries: string[]) => void;
  error?: string;
  placeholder?: string;
  example?: string;
  required?: boolean;
}

const CountriesField: React.FC<CountriesFieldProps> = ({
  label,
  name,
  value,
  onChange,
  error,
  placeholder,
  example,
  required = false,
}) => {
  const addCountry = () => {
    onChange([...value, '']);
  };

  const removeCountry = (index: number) => {
    const newCountries = value.filter((_, i) => i !== index);
    onChange(newCountries);
  };

  const updateCountry = (index: number, newValue: string) => {
    const newCountries = [...value];
    newCountries[index] = newValue;
    onChange(newCountries);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label htmlFor={name} className="block text-sm font-medium text-[var(--primary-deep-blue)]">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
          <span className="text-xs text-[var(--secondary-slate)] ml-2">(At least 1 required)</span>
        </label>
        <button
          type="button"
          onClick={addCountry}
          className="inline-flex items-center gap-1 px-3 py-1 text-sm text-[var(--primary-deep-blue)] bg-[var(--primary-light-blue)] hover:bg-[var(--primary-deep-blue)] hover:text-white rounded-md transition-colors"
        >
          <Plus size={16} />
          Add Country
        </button>
      </div>

      {value.length === 0 ? (
        <div className="text-center py-4 border-2 border-dashed border-[var(--neutral-light-gray)] rounded-lg">
          <p className="text-[var(--secondary-slate)] text-sm mb-2">No countries added</p>
          <button
            type="button"
            onClick={addCountry}
            className="inline-flex items-center gap-1 px-4 py-2 text-sm text-[var(--primary-deep-blue)] bg-[var(--primary-light-blue)] hover:bg-[var(--primary-deep-blue)] hover:text-white rounded-md transition-colors"
          >
            <Plus size={16} />
            Add First Country
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {value.map((country, index) => (
            <div key={index} className="flex items-center gap-2">
              <div className="flex-1">
                <input
                  type="text"
                  value={country}
                  onChange={(e) => updateCountry(index, e.target.value)}
                  placeholder={placeholder || `Country ${index + 1}`}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--primary-deep-blue)] focus:border-transparent ${
                    error ? 'border-red-500' : 'border-[var(--neutral-medium-gray)]'
                  }`}
                />
              </div>
              <button
                type="button"
                onClick={() => removeCountry(index)}
                className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                title="Remove country"
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {example && (
        <p className="text-xs text-[var(--secondary-slate)] italic">
          Example: {example}
        </p>
      )}

      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default CountriesField;
